/*
 * ErrorBoundary — a view that throws must degrade LOUDLY, never blank the
 * console. This is the same honesty law the planes follow: a failure is a
 * first-class red state, not a silent gap. Wrap each plane view so one
 * crashing view never takes down the operator's whole pane of glass.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Human label of the boundaried surface, e.g. the plane name. */
  surface?: string;
}
interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to the console for the SOC; the UI shows it too (below).
    this.setState({ error, info });
  }

  render(): ReactNode {
    const { error, info } = this.state;
    if (!error) return this.props.children;
    const mono =
      '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace';
    return (
      <div
        role="alert"
        aria-live="assertive"
        style={{
          margin: "2rem",
          padding: "1.6rem 1.8rem",
          maxWidth: "70ch",
          border: "1px solid rgba(var(--red-rgb), 0.5)",
          borderLeft: "3px solid var(--red, #ff6b6b)",
          borderRadius: 12,
          background: "rgba(var(--red-rgb), 0.06)",
        }}
      >
        <p
          style={{
            fontFamily: mono,
            fontSize: "0.72rem",
            letterSpacing: "0.14em",
            color: "var(--red, #ff6b6b)",
            margin: 0,
          }}
        >
          ▲ VIEW FAILED TO RENDER
        </p>
        <h2
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 400,
            color: "var(--fg)",
            margin: "0.5rem 0 0.4rem",
          }}
        >
          The {this.props.surface ?? "view"} view crashed
        </h2>
        <p style={{ color: "var(--fg-muted)", margin: "0 0 1rem" }}>
          The console does not hide failures. The substrate is unaffected — this
          is a fault in the view, surfaced rather than swallowed.
        </p>
        <pre
          style={{
            fontFamily: mono,
            fontSize: "0.8rem",
            color: "var(--fg)",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            margin: 0,
          }}
        >
          {error.message || String(error)}
        </pre>
        {info?.componentStack ? (
          <details style={{ marginTop: "0.8rem", color: "var(--fg-faint)" }}>
            <summary style={{ cursor: "pointer", fontFamily: mono, fontSize: "0.72rem" }}>
              Component stack
            </summary>
            <pre
              style={{
                fontFamily: mono,
                fontSize: "0.72rem",
                color: "var(--fg-faint)",
                whiteSpace: "pre-wrap",
              }}
            >
              {info.componentStack.trim()}
            </pre>
          </details>
        ) : null}
      </div>
    );
  }
}
